// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { RefreshCw, Truck } from 'lucide-react'
import { ColumnFilterButton, ColumnFilterChips, funnelToServerOps, type ColumnFilters } from '../../components/common/ColumnFilter'
import { useColumnWidths, ColumnResizeHandle } from '../../components/common/ColumnResize'
import { useStickyState } from '../../components/useStickyState'
import { useNavigate, useSearchParams } from 'react-router-dom'
import SearchableSelect from '../../components/common/SearchableSelect'
import * as api from '../../api/client'
import { useDrugRefValues } from '../../hooks/useDrugRefValues'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import SkeletonRows from '../../components/SkeletonRows'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import CategoryBadge from '../../components/common/CategoryBadge'
import { decodeMetricLink } from '../../lib/metricLink'
import { formatUsDate, formatUsDateTime, formatUsTimestamp, matchesDob, matchesPhone } from '../../api/format'
import { friendlyError } from '../../api/errors'
import {
  ClinicalStatePill, PayerStatePill,
  ErrorBanner, EmptyState, relativeTime,
} from '../../components/workflow/StatePills'
import { useSmartFilters, type FilterDef } from '../../components/SmartFilters'
import { useColumnManager } from '../../components/ColumnManager'
import { siteOfCareLabel } from '../../api/siteOfCare'
import { WorkBenchCell, WorkBenchDateCell, useWorkBenchDefs } from '../../components/workflow/WorkBench'
import { RxRowDrawer, RxDrawerHint, type DrawerField } from '../../components/workflow/RxRowDrawer'
import BulkFieldEditor from '../../components/workflow/BulkFieldEditor'
import { usePharmacy } from './PharmacyLayout'
import { useAuth } from '../../auth/AuthContext'
import { useModules } from '../../auth/ModulesContext'

interface Props {
  /** Comma-separated states to filter on, e.g. "submitted" or "dispensed,out_for_delivery". */
  states: string
  /** Page heading shown above the table. */
  title: string
  /** One-liner shown under the heading. */
  subtitle?: string
  /** Empty-state message when the queue is clear. */
  emptyMessage?: string
  /** When true (All Prescriptions view only), append the pharmacy's custom card fields as columns. */
  customColumns?: boolean
  /** D-TABCOLS-1: system-tab key (intake|insurance|production|ready|hold|refills|all). When the
   * pharmacy has saved a per-tab column override for this key, it drives the columns; otherwise the
   * shared rx_list columns are used exactly as before. Omit to always use the shared columns. */
  tabKey?: string
}

// One sortable column definition. `key` is the field on the row; `label` is
// the header cell; `getValue` returns the value to sort on (defaults to the
// raw field).
interface ColumnDef {
  key: string
  label: string
  getValue?: (rx: api.RxItem) => any
  /** Optional rich cell renderer (wins over getValue for display). */
  render?: (rx: api.RxItem) => React.ReactNode
  /** For custom columns (key `cf:<card>.<col>`): text | date | multi_text. */
  dataType?: string
}

// Format a stored custom-field value for display by its declared type.
function formatCustomValue(raw: string | null | undefined, dataType?: string): string {
  if (raw == null || raw === '') return '—'
  // date AND datetime route through the platform formatter (MM-DD-YYYY, time only when a
  // real clock time is stored — date-only values stay literal, never tz-shifted). A text-typed
  // value that is ISO-timestamp-SHAPED is formatted too: meta-loss fallbacks default to 'text'
  // (RCA-ASP-66 F4) and were rendering raw ISO stamps in the grid.
  if (dataType === 'date' || dataType === 'datetime' || /^\d{4}-\d{2}-\d{2}T\d{2}:/.test(raw)) {
    return formatUsDateTime(raw)
  }
  return raw
}

const COLUMNS: ColumnDef[] = [
  { key: 'rx_number',           label: 'RX #',         getValue: rx => (rx as any).rx_number ?? '' },
  { key: 'patient_name',        label: 'Patient' },
  { key: 'drug_name',           label: 'Drug' },
  { key: 'sig',                 label: 'Sig' },
  { key: 'quantity_authorized', label: 'Quantity',     getValue: rx => Number(rx.quantity_authorized) || 0 },
  { key: 'prescriber_name',     label: 'Prescriber',   getValue: rx => (rx as any).prescriber_name ?? '' },
  // Status column shows the OwlRx-mapped state AND, in the cell, a smaller
  // chip with the original PioneerRx status text. The header reads 'Status'
  // (was 'Clinical') per pharmacy-ops convention — pharmacists track 'Rx
  // status', not 'clinical state'.
  { key: 'state',               label: 'Status' },
  { key: 'payer_state',         label: 'Payer' },
  // WorkBench operational fields — inline-editable colored pills (no need to open the Rx).
  // (key wb_<field_key>; the cell maps to the matching field-def for values + colors.)
  { key: 'wb_pharmacy_queue',     label: 'Pharmacy Queue',     getValue: rx => (rx as any).wb_pharmacy_queue ?? '' },
  { key: 'wb_task_type',          label: 'Task Type',          getValue: rx => (rx as any).wb_task_type ?? '' },
  { key: 'wb_auth_status',        label: 'Auth Status',        getValue: rx => (rx as any).wb_auth_status ?? '' },
  { key: 'wb_order_status',       label: 'Order Status',       getValue: rx => (rx as any).wb_order_status ?? '' },
  { key: 'wb_service_status',     label: 'Service Status',     getValue: rx => (rx as any).wb_service_status ?? '' },
  { key: 'wb_welcome_call_status',label: 'Welcome Call',       getValue: rx => (rx as any).wb_welcome_call_status ?? '' },
  { key: 'wb_consultation_status',label: 'Consultation',       getValue: rx => (rx as any).wb_consultation_status ?? '' },
  { key: 'wb_delivery_call',      label: 'Delivery Call',      getValue: rx => (rx as any).wb_delivery_call ?? '' },
  { key: 'wb_copay_collection',   label: 'CoPay Collection',   getValue: rx => (rx as any).wb_copay_collection ?? '' },
  { key: 'wb_cpap_status',        label: 'CPAP Status',        getValue: rx => (rx as any).wb_cpap_status ?? '' },
  { key: 'wb_patient_category',   label: 'Patient Category',   getValue: rx => (rx as any).wb_patient_category ?? '' },
  { key: 'wb_biopsy',             label: 'Biopsy',             getValue: rx => (rx as any).wb_biopsy ?? '' },
  // Ingested-at-source columns (Patrick 2026-07-22): PioneerRxFullProjection already breaks the
  // envelope into these rx_items fields — the UI reads them directly, no extraction machinery.
  { key: 'priority_type_text',    label: 'Priority (PMS)',     getValue: rx => (rx as any).priority_type_text ?? '' },
  { key: 'current_tx_status_text', label: 'Pharmacy Queue (PMS)', getValue: rx => (rx as any).current_tx_status_text ?? '' },
  { key: 'wb_delivery_status',    label: 'Delivery Status',    getValue: rx => (rx as any).wb_delivery_status ?? '' },
  { key: 'wb_drug_category',      label: 'Drug Category',      getValue: rx => (rx as any).wb_drug_category ?? '' },
  { key: 'wb_copay_needed',       label: 'Priority',           getValue: rx => (rx as any).wb_copay_needed ?? '' },
  // D-SOC-2 (Patrick 2026-09-04): where this Rx is delivered — residential or a facility type — and the facility.
  { key: 'site_of_care_type',     label: 'Site of care',
    getValue: rx => siteOfCareLabel((rx as any).site_of_care_type),
    render: rx => {
      const t = (rx as any).site_of_care_type as string | null
      if (!t) return <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>—</span>
      const res = t === 'residential'
      return <span style={{ fontSize: 11.5, padding: '2px 9px', borderRadius: 20, background: res ? '#F1EFE8' : '#fef3c7', color: res ? '#444441' : '#92400e', fontWeight: 600, whiteSpace: 'nowrap' }}>{siteOfCareLabel(t)}</span>
    } },
  { key: 'site_of_care_facility', label: 'Facility',           getValue: rx => (rx as any).site_of_care_facility ?? '' },
  { key: 'last_state_change_at',label: 'PMS Last Update',  getValue: rx => Date.parse(rx.last_state_change_at) },
  // CV-1d: whole-row workbench change stamp (mig 301) — sortable + filterable.
  { key: 'wb_updated_at', label: 'Workbench Last Update',
    getValue: rx => (rx as any).wb_updated_at ?? '',
    render: rx => {
      const at = (rx as any).wb_updated_at as string | null
      const by = (rx as any).wb_updated_by as string | null
      // "—" when the workbench was never edited by a user. updated_by is stamped only on an
      // actual edit (WorkBenchFunctions); it is NULL on the creation-default row, so a row that
      // merely exists (never touched) shows a dash rather than its creation timestamp.
      if (!at || !by) return <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>—</span>
      return (
        <span style={{ fontSize: 12 }} title={by ?? undefined}>
          {formatUsTimestamp(at)}
          {by ? <span style={{ color: 'var(--color-text-mute)' }}> · {by.split('@')[0]}</span> : null}
        </span>
      )
    } },
]
// D-VIEWCOLS-1: additional standard columns available via the view-builder column
// config (off by default; a pharmacy toggles them on in "Home table columns").
// Date columns get a from/to date-range funnel filter (not a raw-value checkbox list).
const DATE_COL_KEYS = new Set(['last_state_change_at', 'wb_updated_at', 'date_filled_utc', 'date_written', 'wb_task_due_date'])
// `wb_`-prefixed columns are rendered as EDITABLE (choice-dropdown) WorkBenchCells by default.
// These two take a DIFFERENT render (so they stay out of the choice-dropdown path):
//   wb_updated_at    — a stamp, not a field def (E-CV-202: routing it there pilled the raw ISO). Read-only.
//   wb_task_due_date — a DATE field, not a choice. ASP-64: inline edit released — it renders its OWN
//                      editable date input (WorkBenchDateCell), writing via the same /rx/{id}/workbench PUT.
// Anything added here must also have its own `render`, or it falls back to the generic cell.
const WB_NON_EDITABLE = new Set(['wb_updated_at', 'wb_task_due_date'])
const EXTRA_COLUMNS: ColumnDef[] = [
  // getValue returns the RAW ISO value: it feeds sorting (lexicographic ISO = chronological;
  // the old MM-DD-YYYY display string sorted 01-01-2027 before 12-31-2026) and the funnel's
  // toDateKey range compare (the display string round-tripped through a local Date and shifted
  // a day for UTC+ viewers). Display formats via render only.
  { key: 'date_filled_utc', label: 'Date filled',  getValue: rx => (rx as any).date_filled_utc ?? '',
    render: rx => <span style={{ fontSize: 12 }}>{formatUsDate((rx as any).date_filled_utc)}</span> },
  { key: 'date_written',    label: 'Date written', getValue: rx => (rx as any).date_written ?? '',
    render: rx => <span style={{ fontSize: 12 }}>{formatUsDate((rx as any).date_written)}</span> },
  // ASP-64 / ClickUp 86cb27v09: the WorkBench manual due date, as a list column. getValue returns
  // RAW ISO so sorting stays chronological and the from/to date funnel works. EDITABLE inline via
  // WorkBenchDateCell — the same datetime-local + ISO write the Rx-detail WorkBench panel uses, so
  // the two edit surfaces agree and no timezone day-shift is introduced. The cell commits on
  // blur/Enter (not per keystroke) — see the contract on WorkBenchDateCell.
  // No onSaved: deliberate parity with the grid's WorkBenchCell above, which also leaves the row
  // to the list's own refresh rather than invalidating on every inline edit. The consequence is
  // that sort + the date funnel read the pre-edit ISO until the next refresh; adding a refetch
  // per cell edit is a separate call, not part of releasing the ASP-64 hold.
  { key: 'wb_task_due_date', label: 'Task Due Date', getValue: rx => (rx as any).wb_task_due_date ?? '',
    render: rx => (
      <WorkBenchDateCell
        fieldKey="task_due_date"
        rxId={rx.id}
        current={(rx as any).wb_task_due_date ?? null}
      />
    ) },
  // Canonical copay: latest positive rx_claims.patient_pay_amount_paid (same as the label). $0 is a real value.
  { key: 'copay_amount',    label: 'Co-Pay Amount',
    getValue: rx => (rx as any).copay_amount ?? null,
    render: rx => {
      const v = (rx as any).copay_amount
      if (v === null || v === undefined) return <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>—</span>
      return <span style={{ fontSize: 12 }}>{`$${Number(v).toFixed(2)}`}</span>
    } },
  // PMS operational statuses live ON rx_items (import + mirror) — available for every rx,
  // unlike PioneerRx-mapped custom columns which need a stored envelope.
  { key: 'pioneerrx_rx_status', label: 'PMS Rx Status', getValue: rx => (rx as any).pioneerrx_rx_status ?? '' },
  { key: 'pioneerrx_tx_status', label: 'PMS TX Status', getValue: rx => (rx as any).pioneerrx_tx_status ?? '' },
  { key: 'directions_translated', label: 'Translated Directions', getValue: rx => (rx as any).directions_translated ?? '' },
  // ASP-69 (D-UI-1 §2): derived at render from created_at — the SAME anchor as the
  // action_aging card predicate, so "N items > 10 days" and this column agree by
  // definition. Integer -> numeric sort + clean header-filter tokens for free.
  { key: 'days_in_queue', label: 'Days in queue',
    getValue: rx => rx.created_at ? Math.floor((Date.now() - Date.parse(rx.created_at)) / 86_400_000) : null },
]

export default function StationQueue({ states, title, subtitle, emptyMessage, customColumns, tabKey }: Props) {
  const { refresh: refreshCounts } = usePharmacy()
  const navigate = useNavigate()
  const wbDefs = useWorkBenchDefs()

  // Custom card fields (owlrx_table='custom') → columns, All Prescriptions view only.
  // Auto-detected: whatever custom fields the SaaS admin adds on a card show up here.
  // RX-PERF-12 (React Query migration): config is a query — deduped, cached 120s across
  // tab remounts, focus-refetched. All custom card fields become columns (kept per Patrick).
  const customDefsQ = useQuery({
    queryKey: ['field-mappings'],
    queryFn: () => api.listFieldMappings(),
    enabled: !!customColumns,
    staleTime: 120_000,
  })
  const customDefs = useMemo(
    () => (customDefsQ.data?.items ?? []).filter(m => m.owlrx_table === 'custom' && m.is_active && !!m.card_key),
    [customDefsQ.data])

  // Sort state — sortable header click cycles asc → desc → none
  const [deliveryFilter, setDeliveryFilter] = useStickyState<'' | 'delivered' | 'in_transit' | 'exception' | 'none'>(tabKey ? `sq:${tabKey}:delivery` : null, '')
  const [sortKey, setSortKey] = useStickyState<string>(tabKey ? `sq:${tabKey}:sortKey` : null, 'last_state_change_at')
  const [sortDir, setSortDir] = useStickyState<'asc' | 'desc' | null>(tabKey ? `sq:${tabKey}:sortDir` : null, 'desc')
  const [srvPage, setSrvPage] = useState(1)
  const [srvPageSize, setSrvPageSize] = useState(() => preferredPageSize())
  // G-UI-1 / ASP-68: per-column header filters (shared layer). AND-compose with everything
  // above (tabs, top-bar filters, mf deep-links) — they re-slice, never replace.
  const [colFilters, setColFilters] = useStickyState<ColumnFilters>(tabKey ? `sq:${tabKey}:colFilters` : null, {})
  // D-HOME-2 click-through: a home metric card links here with ?mf=<encoded filters>.
  // (Declared up here because serverMode — and therefore the queries — depends on it.)
  const [searchParams, setSearchParams] = useSearchParams()
  const metricLink = useMemo(() => {
    const raw = searchParams.get('mf')
    return raw ? decodeMetricLink(raw) : null
  }, [searchParams])

  // Open claim rejections deep-link (/rx?claim_rejected=1) from the home "Rejected" card.
  // Deliberately NOT an ?mf= metric link: mf filters client-side over the capped fetch and
  // its field allowlist cannot reach rx_claims — a latest-claim predicate needs the server.
  const claimRejected = searchParams.get('claim_rejected') === '1'
  const clearClaimRejected = () => {
    const next = new URLSearchParams(searchParams); next.delete('claim_rejected'); setSearchParams(next, { replace: true })
  }

  // RX-PERF-12: search state is CONTROLLED here (fed into useSmartFilters below) so the
  // mode predicate and the queries can be declared declaratively at the top — the old code
  // broke this cycle with refs that the loader read at call time. Session-only (RX-PERF-11).
  const [searchQ, setSearchQ] = useState('')
  // Search is a SERVER predicate now: every distinct value fires a filtered page query AND its
  // COUNT against the tenant DB. At 250ms (sized when the filter ran in-browser over already-loaded
  // rows) a typed word was up to half a dozen full round trips, and on a 0.5-1 vCore tenant that is
  // the load-multiplier shape behind the 2026-08-06 worker exhaustion. One second means a normal
  // typist issues ONE query for a word instead of one per letter.
  const debouncedSearchQ = useDebouncedValue(searchQ, 1000)

  // ── D-RXFILTER-1 WS4: which filters can the SERVER apply? ────────────────────────────────
  // Capability comes from the server's column registry — never a second list here. That second
  // list is exactly how the old design drifted: a 9-column backend sort whitelist against ~40
  // header funnels, so 31 columns silently fell back to the capped client pipeline and no one
  // could tell which. Cached hard; it changes only on deploy.
  const capsQ = useQuery({
    queryKey: ['rx', 'columns'],
    queryFn: () => api.getRxColumnCaps(),
    staleTime: 600_000,
  })
  const caps = useMemo(
    () => new Map((capsQ.data?.items ?? []).map(c => [c.key, c])),
    [capsQ.data])

  // Translate the header-filter state into server filters. Anything the registry does not accept
  // is reported as UNPUSHABLE rather than quietly dropped — the caller then keeps the bounded
  // client pipeline for correctness and says so, instead of pretending to have filtered everything.
  const { serverFilters, blockedCols } = useMemo(() => {
    const pushed: api.RxFilter[] = []
    const stuck: string[] = []

    // Delivery status. This control is the reason the grid used to abandon pagination: ship_status
    // is derived from rx_shipments, which the list computed AFTER paging, so filtering on it forced
    // the whole client pipeline. It is a registry column now, so it pushes down like anything else.
    // '(blank)' is the registry token for "no value", i.e. the old "No shipment" option.
    if (deliveryFilter) {
      const SHIP_IN_TRANSIT = ['in_transit', 'out_for_delivery', 'label_purchased', 'created', 'picked_up']
      const vals = deliveryFilter === 'in_transit' ? SHIP_IN_TRANSIT
        : deliveryFilter === 'none' ? ['(blank)']
        : [deliveryFilter]
      pushed.push({ key: 'ship_status', op: 'in', values: vals })
    }

    // Dashboard drill-through (?mf=). Every field a metric card can filter on is a registry column,
    // so the payload translates to real server filters instead of being replayed over loaded rows.
    // Anything unrecognised is reported, never dropped — a drill-through that quietly showed the
    // unfiltered list would be worse than an error.
    if (metricLink) {
      for (const f of metricLink.f ?? []) {
        const cap = caps.get(f.field)
        const vals = (f.values ?? []).map(v => String(v))
        if (f.op === 'is_null') { pushed.push({ key: f.field, op: 'in', values: ['(blank)'] }); continue }
        if (!cap?.filterable) { stuck.push(f.field); continue }
        const op: api.RxFilterOp | null =
          (f.op === 'eq' || f.op === 'in') ? 'in'
          : (f.op === 'gte' || f.op === 'gt' || f.op === 'lte' || f.op === 'lt') ? f.op as api.RxFilterOp
          : null
        if (!op || vals.length === 0) { stuck.push(f.field); continue }
        pushed.push({ key: f.field, op, values: op === 'in' ? vals : [vals[0]] })
      }
      const w = metricLink.w
      if (w?.field) {
        const amt = w.amount ?? w.last_days ?? 0
        const ms = w.unit === 'hours' ? 3_600_000 : 86_400_000
        const cutoff = new Date(Date.now() - amt * ms).toISOString().slice(0, 10)
        if (caps.get(w.field)?.filterable) {
          pushed.push({ key: w.field, op: w.mode === 'older_than' ? 'lte' : 'gte', values: [cutoff] })
        } else stuck.push(w.field)
      }
    }

    for (const [key, set] of Object.entries(colFilters)) {
      if (!set || set.size === 0) continue
      const cap = caps.get(key)
      if (!cap?.filterable) { stuck.push(key); continue }
      // One translation for every server-paged list (D-UI-2): value lists, from/to bounds and
      // the contains box all go through it, against the operators the registry admits.
      const t = funnelToServerOps(set, cap.ops)
      for (const p of t.preds) pushed.push({ key, ...p })
      if (t.blocked) stuck.push(key)
    }
    return { serverFilters: pushed, blockedCols: stuck }
  }, [colFilters, caps, deliveryFilter, metricLink])

  // Entity filters store IDs (so the predicate is exact and indexed). Remember the labels the
  // facet returned so chips and ticked boxes read as names, not GUIDs.
  const [filterLabels, setFilterLabels] = useState<Record<string, string>>({})
  const rememberLabels = useCallback((col: string, pairs: { value: string; label: string }[]) => {
    setFilterLabels(prev => {
      let changed = false
      const next = { ...prev }
      for (const o of pairs) {
        const k = `${col}:${o.value}`
        if (next[k] !== o.label) { next[k] = o.label; changed = true }
      }
      return changed ? next : prev
    })
  }, [])
  const labelForFilter = useCallback(
    (col: string, v: string) => filterLabels[`${col}:${v}`], [filterLabels])

  // ── The mode predicate ───────────────────────────────────────────────────────────────────
  // WAS: server-paged only while the user touched NOTHING — no search, no funnel, no delivery
  // filter, no date filter, default sort. The first keystroke or tick dropped to an unpaged
  // 5,000-row fetch, so on ASP a filtered worklist reasoned over roughly the newest half of the
  // tenant behind a banner nobody reads. That was a correctness defect, not a perf one.
  //
  // NOW: search, column filters, date ranges and sort all push into the query, so the list stays
  // paginated through all of them. What is left in the client pipeline is only what genuinely
  // cannot be expressed in the list's WHERE:
  //   - cf:/dr: columns  values live in side tables resolved per page, not in the list query
  //   - deliveryFilter   ship_status is computed in the OUTER query, past the shared WHERE
  //   - metricLink       ?mf= replays a client-side row predicate
  // Those keep the bounded fallback AND the banner that admits it. Everything else is now honest.
  // RX-PERF-6 is preserved: sortDir === null means "no client sort", which server-pages too.
  // ── ONE MODE ────────────────────────────────────────────────────────────────────────────
  // There is no client fallback any more. Every request is server-filtered, server-sorted and
  // server-paged; "next page" is always a network call and the pager total is always the true
  // count. The old dual-mode design is what produced the reported bug: setting Delivery to
  // anything but All silently switched to an unpaged /rx?state=... fetch of up to 5,000 rows and
  // paged them in the browser, so the page buttons did nothing over the network and the totals
  // were a sample presented as a set.
  //
  // Columns the registry cannot express (cf:/dr:, whose values live in per-page side tables) do
  // NOT resurrect that path — their funnel is disabled and says why. A stated limitation beats a
  // hidden one; that is rule R1.
  const sortPushable = sortDir === null || (caps.get(sortKey)?.sortable ?? false)
  // Sort is the one input that can arrive from sticky state naming a column the server cannot
  // order by. Fall back to the server default rather than refusing to load.
  const effSortKey = sortPushable ? sortKey : 'last_state_change_at'
  const effSortDir = sortPushable ? sortDir : 'desc'

    const stateList = useMemo(() => states.split(',').map(s => s.trim()).filter(Boolean), [states])

  // ── The data layer, as queries (RX-PERF-12) ────────────────────────────────────────────
  // Server mode: ONE page, polled every 15s while mounted+visible (React Query pauses
  // intervals in background tabs), focus-refetched. keepPreviousData marks page transitions
  // as placeholder so the tbody shows skeletons without unmounting the shell.
  const pagedQ = useQuery({
    // D-RXFILTER-1: search term, sort and the active column filters are part of the KEY, because
    // they are now part of the QUERY. Previously this key held only the page — which is what made
    // "filtered" and "server-paged" mutually exclusive.
    queryKey: ['rx', 'paged', states, srvPage, srvPageSize, claimRejected,
               debouncedSearchQ.trim(), effSortKey, effSortDir, api.encodeRxFilters(serverFilters).join('|')],
    queryFn: () => api.listRxPaged({
      states: stateList.join(','), page: srvPage, pageSize: srvPageSize,
      // sortDir null = "no client sort": fetch order IS the server default (RX-PERF-6).
      orderBy: effSortDir ? effSortKey : 'last_state_change_at',
      orderDir: effSortDir ?? 'desc',
      q: debouncedSearchQ.trim() || undefined,
      filters: serverFilters,
      claimRejected: claimRejected || undefined,
    }),
    enabled: stateList.length > 0,
    // Review fix (2026-08-14): the 15s tick was sized when a page was 10 rows. At the new
    // 100-row default the same interval is 10x the row volume per open tab against 0.5–1
    // vCore tenant DBs (the 2026-08-06 outage class), so large pages poll at 60s instead —
    // focus refetch still delivers freshness the moment the user looks at the tab.
    refetchInterval: srvPageSize > 25 ? 60_000 : 15_000,
    placeholderData: keepPreviousData,
  })
  // Client mode (any filter/sort/search active): the capped newest-5,000 set for the client
  // pipeline. NO interval (RX-PERF-5 — this is the 818 KB fetch; polling it was the drain);
  // fresh on focus + manual refresh. D-PERF-5: one bounded request for the whole tab.
  // The 5,000-row capped fetch is DELETED. It was the thing the user saw: with Delivery set,
  // GET /rx?state=... returned up to 5,000 rows in one response and the grid paged them locally.
  // Nothing falls back to it now — every predicate the grid can express is a registry filter.

  const activeQ = pagedQ
  // WHAT A DRAWER EDIT JUST WROTE, held here until the list is observed to agree.
  //
  // A drawer save writes correctly, the endpoint returns the new value, and the query cache is
  // updated - all three verified against the running app. The list observer still served the
  // previous rows: neither invalidateQueries nor an explicit refetchQueries({type:'active'})
  // moved it (isFetching stayed false throughout), so the grid kept rendering the stale row.
  // Rather than keep guessing at cache internals, the edit is held where the grid cannot miss
  // it and applied on the way to render.
  //
  // Self-clearing, so it can never mask the server: an entry is dropped the moment a fetched row
  // carries the same value, and any later server-side change simply wins because the row is
  // matched by id and only the fields actually edited here are overlaid.
  const [wbEdits, setWbEdits] = useState<Record<string, Record<string, string | null>>>({})

  const rawItems: api.RxItem[] = pagedQ.data?.items ?? []
  const items: api.RxItem[] = useMemo(() => {
    if (Object.keys(wbEdits).length === 0) return rawItems
    return rawItems.map(r => {
      const patch = wbEdits[String((r as any).id)]
      return patch ? ({ ...r, ...patch } as api.RxItem) : r
    })
  }, [rawItems, wbEdits])

  // Drop an override once the server says the same thing, so this never diverges from the truth.
  useEffect(() => {
    if (Object.keys(wbEdits).length === 0) return
    const next: Record<string, Record<string, string | null>> = {}
    let changed = false
    for (const [id, patch] of Object.entries(wbEdits)) {
      const row = rawItems.find(r => String((r as any).id) === id) as any
      if (!row) { next[id] = patch; continue }
      const remaining = Object.fromEntries(
        Object.entries(patch).filter(([k, v]) => (row[k] ?? null) !== v))
      if (Object.keys(remaining).length > 0) next[id] = remaining as Record<string, string | null>
      if (Object.keys(remaining).length !== Object.keys(patch).length) changed = true
    }
    if (changed) setWbEdits(next)
  }, [rawItems])

  // One authoritative count: the endpoint's exact total for the active predicate.
  const srvTotal = typeof pagedQ.data?.total === 'number' ? pagedQ.data.total : (pagedQ.data?.items?.length ?? 0)
  const srvTotalPages = pagedQ.data?.total_pages ?? 1

  // Foreground load = first fetch of this key, or a page transition (placeholder). Both show
  // tbody skeletons. Background refetches (interval/focus) only flip `refreshing`.
  const loading = activeQ.isLoading || activeQ.isPlaceholderData
  const refreshing = activeQ.isFetching && !loading
  const lastUpdated = activeQ.dataUpdatedAt || null
  // A failed background refetch keeps the last good rows (React Query retains data); the
  // banner only surfaces when there is nothing to show at all. Dismissal is sticky per error.
  const [errorDismissedAt, setErrorDismissedAt] = useState(0)
  const error = activeQ.isError && !activeQ.data && activeQ.errorUpdatedAt > errorDismissedAt
    ? friendlyError(activeQ.error) : null

  useEffect(() => { refreshCounts() }, [refreshCounts])

  // CV-2a: custom values resolve by the TRUE rx_items.id (fill-grain rows carry the fill's id
  // in `id`, which matches nothing in rx_custom_field_values).
  //
  // The FULL-SET resolve is DELETED. It existed to give the client pipeline a value for every
  // loaded row so it could sort and filter on custom columns — in client mode that was 5,000 ids
  // chunked into 25 POSTs on a 0.5-1 vCore tenant DB. Nothing sorts or filters on the client any
  // more, so only the visible page needs values, which is one request (see cfPageQ below).
  const trueId = (i: any) => i.rx_item_id ?? i.id
  // The id a WORKBENCH edit must be posted to — the row's own id, which in a fill-grain row is
  // the fill id. Deliberately NOT trueId(): custom fields and shipment meta are prescription-
  // level and rightly use that, but a WorkBench edit belongs to the fill it was made on.
  const wbTargetId = (i: any) => i.id ?? i.rx_item_id
  const hasCustomCols = !!customColumns && customDefs.length > 0

  // Smart cascading filters
  // Custom columns (All view only): one sortable column per custom card field.
  const customCols: ColumnDef[] = useMemo(() => (customColumns ? customDefs : []).map(m => {
    const vkey = `${m.card_key}.${m.owlrx_column}`
    return {
      key: `cf:${vkey}`,
      label: m.owlrx_label.trim() || m.owlrx_column,
      dataType: m.data_type,
      // No getter: cell display reads the page-level resolve (cfDisp), and nothing sorts or
      // filters these on the client any more. A full-set getter would only invite that back.
    }
  }), [customColumns, customDefs])

  // D-VIEWCOLS-1: pharmacy-level home-table column config (order/visibility/labels).
  // RX-PERF-12: one query owning the two-step resolve.
  // D-TABCOLS-1 safety invariant: use a per-tab override (rx_list:<tabKey>) ONLY when the pharmacy
  // has actually saved one; otherwise fall back to the shared rx_list exactly as before. So with no
  // override in existence, behavior is byte-identical to today for every tab and every pharmacy.
  const listCfgQ = useQuery({
    queryKey: ['rx-view-layout', 'list', tabKey ?? '(shared)'],
    queryFn: async () => {
      if (tabKey) {
        try {
          const ov = await api.getRxViewLayout(undefined, `rx_list:${tabKey}`)
          const hasOverride = ov?.source === 'custom' && ((ov.cards ?? []) as any[]).some(c => c.card_key === 'list_columns')
          if (hasOverride) return ov
        } catch { /* fall through to shared */ }
      }
      return api.getRxViewLayout(undefined, 'rx_list')
    },
    staleTime: 120_000,
  })
  const listCfg = listCfgQ.data ?? null
  // D-DRUGREF-3b: batch DrugReference values for configured drug_reference columns (module-gated).
  // RX-PERF-13 (audit F3): resolve for the VISIBLE PAGE, not all loaded rows — the old
  // items-keyed call posted 5,000 ids in one unchunked request on every client-mode load.
  // Ids arrive via state set from an effect below the pager (the row pipeline runs before
  // the pager exists, so this hook cannot read pageRows directly). A sort or header filter
  // on a dr: column escalates to the full loaded set — those genuinely rank every row.
  const drugRefWanted = useMemo(() => ((((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.fields ?? []) as any[]).some((f: any) => f.owlrx_table === 'drug_reference')), [listCfg])
  // Always page-scoped now. The old escalation to the full loaded set existed to rank every row
  // for a client-side dr: sort/filter; with those gone it would only be waste (and it was the call
  // that hit the server's 500-id cap — see WS0).
  const [drIds, setDrIds] = useState<string[]>([])
  const drugRefVals = useDrugRefValues(drIds, drugRefWanted)
  // D-VIEWCOLS-2a: per-column provider-view exclusions live on the list card's config.
  const { me, hasPermission } = useAuth()
  const canQuickShip = hasPermission('shipping.create')
  // ASP-60: multi-Rx selection -> one bulk shipment. First selected = primary; the rest are
  // admitted through the scan-check gate inside the drawer (never blind-added).
  const [selShip, setSelShip] = useState<string[]>([])
  const toggleShipSel = (id: string) => setSelShip(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id])
  // RULE (Patrick 2026-07-23): one shipment = ONE patient. A cross-patient selection is an ERROR
  // at selection time; shipping anyway needs an explicit override here AND an audited per-Rx
  // reason in the drawer's mismatch dialog (never a silent merge).
  const [shipOverride, setShipOverride] = useState(false)
  // BULK-1: meta also carries the TRUE rx_items.id (CV-2a) — bulk field writes must never
  // use the fill-grain display id.
  const [selShipMeta, setSelShipMeta] = useState<Record<string, { pid: string; name: string; tid: string; wid: string }>>({})
  const selShipPatientCount = useMemo(
    () => new Set(selShip.map(id => selShipMeta[id]?.pid).filter(Boolean)).size, [selShip, selShipMeta])
  const shipMultiPatient = selShipPatientCount > 1
  useEffect(() => { if (!shipMultiPatient) setShipOverride(false) }, [shipMultiPatient])
  const isProvider = me?.role_name === 'provider' || me?.role_name === 'provider_nurse'
  // BULK-1: bulk-change of OwlRx-owned fields (custom + workbench). Any non-provider staff;
  // the selection column shows for either capability.
  // INVARIANT 4 (BioAccess byte-for-byte): gated on the 'queues' module — the module that owns
  // custom fields / workbench / custom views. BioAccess doesn't have it, so its Prescriptions
  // list renders exactly as before this change (checkboxes still appear only via shipping).
  const { isDisabled: isModuleDisabled } = useModules()
  const canBulkEdit = !isProvider && !isModuleDisabled('queues')
  const canSelect = canQuickShip || canBulkEdit
  const [showBulk, setShowBulk] = useState(false)
  const queryClient = useQueryClient()
  // TWO aligned lists, because the bulk dialog writes at two grains in one pass: custom-field
  // values are keyed on the PRESCRIPTION, a WorkBench value on the FILL it belongs to.
  // De-duplicating on the prescription would collapse sibling fills into one row and silently
  // drop the others, so the pairs are de-duplicated on the WORKBENCH id.
  const bulkPairs = useMemo(() => {
    const seen = new Set<string>()
    const out: { rxId: string; wbId: string }[] = []
    for (const id of selShip) {
      const meta = selShipMeta[id]
      const wbId = meta?.wid ?? id
      if (seen.has(wbId)) continue
      seen.add(wbId)
      out.push({ rxId: meta?.tid ?? id, wbId })
    }
    return out
  }, [selShip, selShipMeta])
  const bulkIds = useMemo(() => bulkPairs.map(p => p.rxId), [bulkPairs])
  const bulkWbIds = useMemo(() => bulkPairs.map(p => p.wbId), [bulkPairs])
  const provHidden = useMemo(() => new Set(((((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.config as any)?.provider_hidden ?? []) as string[])), [listCfg])
  // D-VIEWCOLS-2b: per-column sub-text (second line) suppression from the same config.
  const subHidden = useMemo(() => new Set(((((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.config as any)?.subtext_hidden ?? []) as string[])), [listCfg])
  // Full column set: base/wb columns with custom columns inserted before the trailing "Last change".
  const columns: ColumnDef[] = useMemo(() => {
    // D-VIEWCOLS-1: when the pharmacy saved a home-table column config, it drives
    // order + visibility + labels. wb_* columns keep their existing module-driven
    // behavior and are appended after the configured set. No config -> unchanged.
    // Copay repoint (ASP-only, invariant 4 keeps BioAccess untouched): the list copay is a
    // pio-mapped Claim[0].PatientPayAmountPaid column, which disagrees with the label on
    // multi-claim Rx. Render the canonical latest-positive copay_amount (identical source to
    // the label/shipping) instead. $0 is a real value; only a true no-claim row shows "—".
    const isAsp = (me?.pharmacy_id || '').toUpperCase() === '5727A2D8-81B8-402B-9802-5F8480FD9508'
    const fixCopay = (cols: ColumnDef[]): ColumnDef[] => !isAsp ? cols : cols.map(c =>
      (c.key.endsWith('.pio_patientpayamountpaid') || /patient.?pay.?amount.?paid/i.test(c.label || ''))
        ? { ...c,
            getValue: (rx: any) => (rx as any).copay_amount ?? null,
            render: (rx: any) => {
              const v = (rx as any).copay_amount
              return (v === null || v === undefined)
                ? <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>—</span>
                : <span style={{ fontSize: 12 }}>{`$${Number(v).toFixed(2)}`}</span>
            } }
        : c)
    const cfgFields: any[] = (listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.fields ?? []
    if (cfgFields.length > 0) {
      const registry = [...COLUMNS, ...EXTRA_COLUMNS]
      const cfgCols = cfgFields.filter(f => f.visible).map(f => {
        const def = f.owlrx_table === 'drug_reference'
          ? ({ key: `dr:${f.owlrx_column}`, label: f.owlrx_column,
               getValue: (rx: api.RxItem) => String((drugRefVals[(rx as any).rx_item_id ?? rx.id] as any)?.[f.owlrx_column] ?? ''),
               render: f.owlrx_column === 'category'
                 ? (rx: api.RxItem) => <CategoryBadge name={drugRefVals[(rx as any).rx_item_id ?? rx.id]?.category} color={drugRefVals[(rx as any).rx_item_id ?? rx.id]?.category_color} />
                 : undefined } as ColumnDef)
          : f.owlrx_table === 'custom'
          // RCA-ASP-66 F4: a saved custom column renders from the cfg row itself when the
          // mapping lookup misses (e.g. historically-wiped card_key) — structure never
          // silently vanishes from the portal; values flow once the mapping heals on Save.
          ? (customCols.find(c => c.key.endsWith(`.${f.owlrx_column}`))
             ?? ({ key: `cf:list_columns.${f.owlrx_column}`, label: f.owlrx_label || f.owlrx_column,
                   dataType: f.data_type || 'text' } as ColumnDef))
          : registry.find(c => c.key === f.owlrx_column)
        return def ? { ...def, label: (f.owlrx_label || '').trim() || def.label } : null
      }).filter((c): c is ColumnDef => !!c)
      // RCA-ASP-66 ALIGNMENT (Patrick 2026-07-22, Lucas/Nadia sign-off): when a saved cfg
      // exists it is the ONLY column source — zero auto-appends of any kind. The old wb
      // rest-append injected workbench twins ('Pharmacy Queue', 'Priority', 'Delivery
      // Status', 'Drug Category') behind the builder's back, making admin deletions
      // structurally impossible to observe. Builder list == portal list, by construction.
      const rest: ColumnDef[] = []
      // D-VIEWCOLS-2a: columns the pharmacy admin hid from the provider view.
      const all = cfgCols.length > 0 ? [...cfgCols, ...rest] : null
      if (all) return fixCopay(isProvider ? all.filter(c => !provHidden.has(c.key)) : all)
    }
    const base = customCols.length === 0 ? COLUMNS : [...COLUMNS.slice(0, -1), ...customCols, COLUMNS[COLUMNS.length - 1]]
    return fixCopay(isProvider ? base.filter(c => !provHidden.has(c.key)) : base)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customCols, listCfg, isProvider, provHidden, drugRefVals, me])

  const rxFilterDefs: FilterDef[] = useMemo(() => [], [])

  const rxSearchFields = useMemo(() => [
    (rx: any) => rx.rx_number,
    (rx: any) => rx.patient_name,
    (rx: any) => rx.patient_mrn,
    (rx: any) => rx.drug_name,
    (rx: any) => rx.sig,
    (rx: any) => rx.prescriber_name,
  ], [])
  // ASP-56: DOB (all 6 formats) + phone-digit matching, same as the patient search.
  // RX-PERF-13 (audit F5): phone matching needs 3+ digits — "13" was substring-matching
  // half the tenant's phone numbers, which is why a 2-char search "matched" 5,000+ rows.
  const rxSearchPredicates = useMemo(() => [
    (q: string, rx: any) => matchesDob(q, rx.patient_dob ?? rx.date_of_birth),
    (q: string, rx: any) => q.replace(/\D/g, '').length >= 3 && matchesPhone(q, rx.patient_phone ?? rx.primary_phone),
  ], [])

  // D-HOME-2: metricLink itself is declared at the top (the mode predicate needs it);
  // here we apply it as the pinned pre-filter over the loaded rows.
  // The ?mf= payload is translated into server filters above, so there is no client replay.
  const clearMetricLink = () => {
    const next = new URLSearchParams(searchParams); next.delete('mf'); setSearchParams(next, { replace: true })
  }

  // RX-PERF-12: search state is controlled from the top of the component (declared beside
  // the mode predicate); the hook renders the box and applies the client filter.
  // RX-PERF-13: minSearchLen 2 aligns the client filter with the server ?q= threshold
  // (audit F5); showCount false removes the loaded-subset "X of Y" counter that
  // contradicted the pager's union total (audit F4) — the pager + the partial-set banner
  // are the one authoritative count on this surface.
  const { filterBar } = useSmartFilters(items, rxFilterDefs, { searchFields: rxSearchFields, searchPredicates: rxSearchPredicates, persistKey: tabKey ? `sq:${tabKey}:smart` : undefined, persistSearch: false, searchState: [searchQ, setSearchQ], minSearchLen: 2, showCount: false })

  // D-RXFILTER-1 WS4 — the beyond-cap search union is GONE.
  //
  // It existed because /rx?state= returned only the newest 5,000 rows, so client search could not
  // see older prescriptions (ASP-75: Rx 67735 was invisible in the queue while the patient page
  // showed it fine). The workaround fired a SECOND unpaged request with ?q= and merged its hits
  // into the row source, deduped by id, with a banner explaining the arithmetic.
  //
  // Now that search is a parameter of the paged query itself, matches are found by the SERVER
  // across the whole tenant and arrive already paged. One request, an exact total, no union, no
  // dedupe, and no banner that has to explain why two numbers disagree.

  // RX-DRAWER-1: ?rxd=<row id> opens the right-click row drawer. Keyed by Rx id, NOT grid
  // position — the 15s refresh keeps flowing and re-sorting underneath; the drawer just
  // re-finds its row on each render and keeps the last-known snapshot if the row leaves
  // the loaded window, so it never blanks mid-work. Renders from already-loaded row data:
  // ZERO extra API calls. URL param → reload / deep link / NEXT-1 login-return restore it.
  const drawerId = searchParams.get('rxd')
  const lastDrawerRow = useRef<api.RxItem | null>(null)
  // THE DRAWER READS ITS OWN WORKBENCH, not the grid's copy of it.
  //
  // wbFields used to be built purely from the list row, so the drawer could only ever be as
  // fresh as the last list fetch - and it showed a MIX: Service Status from the row it was
  // opened on beside a Welcome Call left over from an earlier fetch. Opening a record is exactly
  // when a pharmacist expects to see the truth, and the endpoint that knows it was never called.
  //
  // GET /rx/{id}/workbench resolves the id the same way the PUT does (ResolveFillAsync), so at
  // fill grain this returns the FILL's effective values - the same thing the save writes back to.
  // staleTime 0 and refetchOnMount make every open a fresh read; it is one row by primary key.
  const drawerWbQ = useQuery({
    queryKey: ['rx', 'workbench', drawerId],
    queryFn: () => api.apiFetch(`/rx/${drawerId}/workbench`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r)),
    enabled: !!drawerId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const drawerWb: Record<string, any> | null = drawerWbQ.data?.workbench ?? null

  const drawerRx = useMemo(() => {
    if (!drawerId) { lastDrawerRow.current = null; return null }
    const found = items.find(r => r.id === drawerId) ?? null
    if (found) lastDrawerRow.current = found
    return found ?? (lastDrawerRow.current?.id === drawerId ? lastDrawerRow.current : null)
  }, [drawerId, items])
  const openDrawer = (id: string) => {
    const next = new URLSearchParams(searchParams); next.set('rxd', id); setSearchParams(next)
  }
  const closeDrawer = useCallback(() => {
    const next = new URLSearchParams(searchParams); next.delete('rxd'); setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Honor the pharmacy's Rx-header customization: identity fields removed from the
  // Rx header are hidden in this queue too (subtitles inline; whole columns filtered).
  const rxLayoutQ = useQuery({
    queryKey: ['rx-view-layout', 'rx'],
    queryFn: () => api.getRxViewLayout(undefined, 'rx'),
    staleTime: 120_000,
  })
  const rxLayout = rxLayoutQ.data ?? null
  const hdrHidden = useMemo(() => ({
    mrn: api.headerFieldHidden(rxLayout, 'mrn'),
    refill: api.headerFieldHidden(rxLayout, 'refill_number'),
    rx_number: api.headerFieldHidden(rxLayout, 'rx_number'),
    prescriber: api.headerFieldHidden(rxLayout, 'prescriber'),
  }), [rxLayout])

  // RCA-ASP-66: the manager operates on stable column KEYS (labels are display-only) —
  // label keying collapsed same-label duplicates and outlived admin deletions.
  const allColKeys = useMemo(() => columns.map(c => c.key), [columns])
  const colLabels = useMemo(() => Object.fromEntries(columns.map(c => [c.key, c.label])), [columns])
  const { visibleColumns: visColKeys, managerUI, toggleButton } = useColumnManager(
    `rx_queue_${states}`, allColKeys, colLabels
  )
  // Render columns in the ORDER the column manager provides (visColLabels is already reordered +
  // hidden-filtered). Mapping label->def in that order is what makes up/down reordering take effect;
  // the previous `columns.filter(...)` kept the original array order and ignored the reorder.
  const visibleCols = useMemo(() => {
    const byKey = new Map<string, ColumnDef>()
    for (const c of columns) if (!byKey.has(c.key)) byKey.set(c.key, c)
    return visColKeys
      .map(k => byKey.get(k))
      .filter((c): c is ColumnDef =>
        !!c
        && !(c.key === 'rx_number' && hdrHidden.rx_number)
        && !(c.key === 'prescriber_name' && hdrHidden.prescriber))
  }, [columns, visColKeys, hdrHidden])

  // D-VIEWS-COLUMN-RESIZE (86cb27wub): per-tab column widths, durable across sessions.
  // Keyed on tabKey so every system view keeps its own layout; null when there is no tab key
  // (widths then live for the mount only, exactly like the other view state here).
  const colResize = useColumnWidths(tabKey ? `sq:${tabKey}` : null, visibleCols.map(c => c.key))

  // Apply per-column single-date filters (From-type = on/after, To-type = on/before) before sorting.
  // ── The client filter/sort pipeline is GONE ────────────────────────────────────────────
  // dateFiltered / deliveryFiltered / headerFiltered / the client sort all used to run here over
  // whatever rows happened to be loaded. Every one of them is now a predicate in the query, so the
  // rows that arrive ARE the answer: filtered, sorted and paged by the database, with a total that
  // counts the whole tenant rather than the sample in memory.

  // Rows arrive filtered and sorted; nothing is re-derived on the client.
  const visibleItems = items

  // The per-column distinct scan over loaded rows is gone with the pipeline: option lists come
  // from /rx/facets, which reads the column's own authority instead of the current page.

  // RX-PERF-12: fetch triggering is fully declarative now — the queries re-key on
  // states/page/size and flip on `enabled`. Only the page-position rules remain:
  // Re-entering server mode starts at page 1 (you were filtering; resume from the top).
  // Reset server pagination to page 1 when the tab (states) changes.
  useEffect(() => { setSrvPage(1) }, [states])
  // Review fix (2026-08-14): toggling the rejection filter changes the dataset without a
  // serverMode transition, so a stale page index survived (clear the pill on page 3 → an
  // arbitrary mid-list slice of the UNfiltered set, or an empty page in the other direction).
  useEffect(() => { setSrvPage(1) }, [claimRejected])
  // D-RXFILTER-1: search, sort and filters are now part of the QUERY, so any of them changing
  // changes the result set — page 3 of the old set is meaningless against the new one.
  const srvPredicateSig = `${debouncedSearchQ.trim()}|${sortKey}|${sortDir}|${api.encodeRxFilters(serverFilters).join('|')}`
  useEffect(() => { setSrvPage(1) }, [srvPredicateSig])
  // A size change resets to page 1 in the SAME batched update — one render, one query key
  // change, one fetch. (Shrinking 100→10 while deep in the list would otherwise strand the
  // pager past the last page.)
  const setSrvPageSizeReset = useCallback((n: number) => { rememberPageSize(n); setSrvPage(1); setSrvPageSize(n) }, [])

  // In server mode the fetched `items` ARE the page → drive the table + <Pager> from server state
  // (with the endpoint's exact total); otherwise use the client pager. One set of names downstream.
  const pageRows = items
  const page = srvPage
  const setPage = setSrvPage
  const pageSize = srvPageSize
  const setPageSize = setSrvPageSizeReset
  const total = srvTotal
  const totalPages = srvTotalPages

  // RX-PERF-13 (audit F1/F6): ONE page-sized custom-value resolve for what is actually on
  // screen. Server mode: identical to the old per-page behavior (10 ids). Client mode: 1
  // request instead of 25 — and because pageRows is taken AFTER the beyond-cap search hits
  // are merged in, those rows finally get their custom columns populated.
  const pageTrueIdsKey = useMemo(
    () => Array.from(new Set(pageRows.map(trueId))).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageRows])
  const cfPageKey = hasCustomCols ? pageTrueIdsKey : ''
  const cfPageQ = useQuery({
    queryKey: ['rx', 'cfv-page', cfPageKey],
    queryFn: () => api.batchCustomFieldValues('rx', cfPageKey.split(',')),
    enabled: !!cfPageKey,
    staleTime: 60_000,
  })
  // Cell display: page-level values first, full-set values as fallback while a page loads.
  const cfDisp = (rx: any): Record<string, string | null> | undefined =>
    cfPageQ.data?.values?.[trueId(rx)]
  // Drug-ref ids for the hook declared above the column registry: visible page normally,
  // full loaded set only while a dr: sort/filter needs every row ranked.
  useEffect(() => {
    setDrIds(prev => prev.join(',') === pageTrueIdsKey ? prev : (pageTrueIdsKey ? pageTrueIdsKey.split(',') : []))
  }, [pageTrueIdsKey])

  function clickHeader(key: string) {
    if (sortKey !== key) {
      setSortKey(key); setSortDir('asc'); return
    }
    if (sortDir === 'asc') { setSortDir('desc'); return }
    if (sortDir === 'desc') { setSortDir(null); return }
    setSortDir('asc')
  }

  function sortIcon(key: string) {
    if (sortKey !== key || !sortDir) return ''
    return sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={h2Style}>{title}</h2>
          {subtitle && <p style={subtitleStyle}>{subtitle}</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={mutedStyle} aria-live="polite">
            {refreshing
              ? 'Refreshing…'
              : lastUpdated ? `Updated ${new Date(lastUpdated).toLocaleTimeString()}` : ''}
          </span>
          <button
            type="button"
            onClick={() => activeQ.refetch()}
            disabled={loading || refreshing}
            style={refreshBtnStyle}
            aria-label="Refresh prescriptions now"
            title="Auto-refreshes every 20s · click to refresh now"
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} onClose={() => setErrorDismissedAt(Date.now())} />}

      {/* RX-PERF-11: the toolbar (search + filters) must NEVER unmount during a load. The old
          `!loading && items.length > 0` gate hid it on first paint AND blinked it away on the
          first search keystroke (the mode-flip foreground reload) — the "search disappears for
          a second" report. Visible whenever loading or once any rows exist; it only stays
          hidden for a genuinely empty idle queue.
          RX-PERF-13 (audit F7): ALSO pinned open whenever a search or filter is active — the
          input the user is typing into must survive every transient query state (the mode-flip
          frame where cappedQ is enabled but not yet fetching, and a failed fetch with no rows),
          or the box unmounts mid-word and focus is lost. */}
      {(loading || items.length > 0 || searchQ.trim() !== '' || deliveryFilter !== '' || claimRejected ||
        Object.values(colFilters).some(s => s.size > 0)) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
          <div style={{ flex: 1 }}>
            {claimRejected && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600,
                  padding: '4px 10px', borderRadius: 999, border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)', color: 'var(--color-text)',
                }}>
                  Open claim rejections{typeof srvTotal === 'number' ? ` · ${srvTotal.toLocaleString()} match` : ''}
                  <button onClick={clearClaimRejected} aria-label="Clear the open claim rejections filter"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, color: 'var(--color-text-muted)', padding: 0, lineHeight: 1 }}>✕</button>
                </span>
              </div>
            )}
            {metricLink && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600,
                  padding: '4px 10px', borderRadius: 999, border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)', color: 'var(--color-text)',
                }}>
                  {metricLink.t} · {srvTotal.toLocaleString()} match
                  <button onClick={clearMetricLink} aria-label={`Clear the ${metricLink.t} filter`}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, color: 'var(--color-text-muted)', padding: 0, lineHeight: 1 }}>✕</button>
                </span>
              </div>
            )}
            {filterBar}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--color-text-muted)' }}>
              Delivery
              <SearchableSelect value={deliveryFilter} onChange={v => setDeliveryFilter(v as any)}
                ariaLabel="Filter by delivery status"
                options={[
                  { value: '', label: 'All' },
                  { value: 'delivered', label: '✓ Delivered' },
                  { value: 'in_transit', label: '⛟ In transit' },
                  { value: 'exception', label: '⚠ Exception' },
                  { value: 'none', label: 'No shipment' },
                ]}
                style={{ minWidth: 150 }} />
            </label>
          </div>
          {toggleButton}
        </div>
      )}
      {managerUI}

      <div style={cardStyle}>
        {/* RX-PERF-4 (skeleton loading): a load no longer swaps the whole card for a spinner —
            that blanked the table (headers, pager, scroll position) on every search/mode change.
            The shell stays mounted; only the tbody shows skeleton rows while a foreground load
            is in flight. Silent refreshes never touch `loading`, exactly as before. */}
        {!loading && visibleItems.length === 0 ? (
          <EmptyState message={items.length === 0 ? (emptyMessage ?? 'Queue is clear.') : 'No prescriptions match your search.'} />
        ) : (
          <>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize}
                 onPage={setPage} onPageSize={setPageSize} label="prescriptions" />
          {blockedCols.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, padding: '7px 10px', marginBottom: 8, borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f' }}>
              {/* There is no capped mode left to warn about. What CAN still happen is a filter the
                  server has no expression for (a cf:/dr: custom column, or a drill-through field
                  outside the registry). Rather than silently ignoring it — or silently loading the
                  whole tenant to honour it — the request is refused and named here. */}
              <span>
                <b>{blockedCols.map(k => colLabels[k] ?? k).join(', ')}</b>
                {blockedCols.length > 1 ? ' cannot be filtered' : ' cannot be filtered'} on the server yet,
                so {blockedCols.length > 1 ? 'they are' : 'it is'} not applied. Everything else below is filtered across all{' '}
                {srvTotal.toLocaleString()} prescriptions.
              </span>
            </div>
          )}
          <div style={tableScrollStyle}>
                    <ColumnFilterChips filters={colFilters}
                      labels={Object.fromEntries(columns.map(c => [c.key, c.label]))}
                      labelFor={labelForFilter}
                      onClear={k => setColFilters(f => ({ ...f, [k]: new Set() }))}
                      onClearAll={() => setColFilters({})} />
                    {/* BULK-1: one bar for both selection actions. Shipping keeps its exact
                        historical behavior (canQuickShip, >= 2 rows); Bulk change appears for any
                        non-provider from the first ticked row. */}
                    {selShip.length >= 1 && (canBulkEdit || (canQuickShip && selShip.length >= 2)) && (() => {
                      const shipActions = canQuickShip && selShip.length >= 2
                      return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', marginBottom: 8, border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface-raised, var(--color-surface))' }}>
              <Truck size={14} />
              <span style={{ fontSize: 13 }}>{selShip.length} prescription{selShip.length === 1 ? '' : 's'} selected</span>
              {shipActions && shipMultiPatient && (
                <span style={{ fontSize: 12.5, color: '#92400e', fontWeight: 600 }}>
                  ⚠ {selShipPatientCount} different patients — a shipment can only contain one patient's Rx
                </span>
              )}
              {shipActions && shipMultiPatient && !shipOverride && (
                <button type="button" onClick={() => setShipOverride(true)}
                  style={{ border: '1px solid #f59e0b', background: '#fffbeb', color: '#b45309', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Override
                </button>
              )}
              {shipActions && (
              <button type="button"
                disabled={shipMultiPatient && !shipOverride}
                title={shipMultiPatient && !shipOverride ? "Selection contains more than one patient — Override to continue" : undefined}
                onClick={() => navigate(`/shipments?ship_rx=${selShip[0]}&ship_rx_more=${selShip.slice(1).join(',')}${shipOverride ? '&allow_mismatch=1' : ''}`)}
                style={{ marginLeft: 'auto', border: '1px solid var(--color-border)', background: (shipMultiPatient && !shipOverride) ? 'var(--color-text-mute, #9ca3af)' : 'var(--color-accent, #1a56db)', color: '#fff', borderRadius: 6, padding: '5px 12px', cursor: (shipMultiPatient && !shipOverride) ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                Create shipment ({selShip.length})
              </button>
              )}
              {canBulkEdit && (
                <button type="button" onClick={() => setShowBulk(true)}
                  style={{ ...(shipActions ? {} : { marginLeft: 'auto' }), border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  Bulk change fields ({selShip.length})
                </button>
              )}
              <button type="button" onClick={() => setSelShip([])}
                style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 13 }}>
                Clear
              </button>
            </div>
                      )
                    })()}
          <RxDrawerHint />
          <table ref={colResize.tableRef} className={colResize.tableClass}
            style={{
              ...tableStyle,
              tableLayout: colResize.tableLayout,
              // Once widths are applied the table sizes to their SUM. Keeping tableStyle's
              // minWidth: 2200 here would let the browser hand back every pixel a drag removes.
              ...(colResize.totalWidth
                ? { width: colResize.totalWidth + (canSelect ? 58 : 0), minWidth: 0 }
                : null),
            }}>
            {/* D-VIEWS-COLUMN-RESIZE: widths bind through <colgroup> under table-layout:fixed.
                The select column keeps its fixed 58px and is not resizable. */}
            <colgroup>
              {canSelect && <col style={{ width: 58 }} />}
              {visibleCols.map(col => <col key={col.key} style={{ width: colResize.widthFor(col.key) }} />)}
            </colgroup>
            <thead>
              <tr>
                {canSelect && <th style={{ ...thStyle, width: 58 }} aria-label="Row select" />}
                {visibleCols.map((col, ci) => (
                  <th
                    key={col.key}
                    style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => clickHeader(col.key)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clickHeader(col.key) } }}
                    tabIndex={0}
                    role="columnheader"
                    aria-sort={sortKey === col.key
                      ? (sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none')
                      : 'none'}
                  >
                    {col.label}{sortIcon(col.key)}{' '}
                    {(() => {
                      // D-RXFILTER-1: a column the registry can facet asks the SERVER for its
                      // options — the configured choice list, a capped distinct, or a typeahead
                      // over the entity. Columns the registry does not own (cf:/dr:, resolved from
                      // side tables per page) keep the loaded-rows source, and their funnel is the
                      // one that still forces the bounded fallback. Same component either way.
                      // A column the registry owns asks the SERVER for its options. A column it
                      // does not own (cf:/dr:, whose values live in per-page side tables) gets NO
                      // funnel at all — not a loaded-rows funnel, because that is precisely the
                      // sample-presented-as-a-set behaviour this change removes, and not a silent
                      // fallback to an unpaged fetch. The column still displays; only filtering on
                      // it is unavailable, and the title says so.
                      const cap = caps.get(col.key)
                      const srvFacet = !!cap?.facetable
                      const capDate = cap?.klass === 'date'
                      const capNumeric = cap?.klass === 'numeric'
                      // D-UI-2: a column the registry filters by CONTAINS and never lists (an Rx
                      // number, an MRN, free-text directions) gets the funnel with a text box in
                      // it. It used to open an empty value list that filtered nothing.
                      const isText = !!cap?.filterable && !srvFacet && !capDate && !capNumeric && cap.ops.includes('contains')
                      if (!cap?.filterable && !srvFacet) return (
                        <span title={`${col.label} can only be filtered once this column is supported server-side.`}
                          aria-label={`${col.label} filter unavailable`}
                          style={{ display: 'inline-block', width: 14, opacity: 0.25, cursor: 'not-allowed', fontSize: 11 }}>⌀</span>
                      )
                      return (
                    <ColumnFilterButton label={col.label}
                      fetchOptions={srvFacet ? (term: string) => api.getRxFacet({
                        column: col.key,
                        q: term,
                        states: stateList.join(','),
                        // Spreadsheet semantics, preserved: a column's own popover keeps its full
                        // option list while that column is filtered; every OTHER active filter
                        // narrows it, so an option that would return nothing is not offered.
                        filters: serverFilters.filter(f => f.key !== col.key),
                        claimRejected: claimRejected || undefined,
                      }) : undefined}
                      labelFor={srvFacet ? (v: string) => labelForFilter(col.key, v) : undefined}
                      onLabels={srvFacet ? (pairs) => rememberLabels(col.key, pairs) : undefined}
                      isDate={capDate || DATE_COL_KEYS.has(col.key) || ['date', 'datetime'].includes((col as any).dataType ?? '')}
                      isNumeric={capNumeric}
                      isText={isText}
                      selected={colFilters[col.key] ?? new Set()}
                      onChange={next => { setColFilters(f => ({ ...f, [col.key]: next })); setPage(1) }} />
                      )
                    })()}
                    <ColumnResizeHandle colKey={col.key} colIndex={ci} api={colResize} label={col.label} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows rows={Math.min(pageSize, 10)} cols={visibleCols.length + (canQuickShip ? 1 : 0)} />
              ) : pageRows.map((rx) => (
                <tr
                  key={rx.id}
                  style={trStyle}
                  onDoubleClick={() => navigate(`/rx/${rx.id}`)}
                  onContextMenu={(e) => { e.preventDefault(); openDrawer(rx.id) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/rx/${rx.id}`) }}
                  tabIndex={0}
                  role="button"
                  title="Double-click to open · right-click for quick actions"
                  aria-label={`Open Rx for ${rx.patient_name} — ${rx.drug_name} (double-click to open)`}
                >
                  {canSelect && (
                    <td style={{ ...tdStyle, width: 58, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select Rx`}
                        checked={selShip.includes(rx.id)}
                        onChange={() => {
                          // tid = the PRESCRIPTION (custom fields, shipping). wid = the row's own
                          // id, which at fill grain is the FILL - what a WorkBench write must go to.
                          setSelShipMeta(m => ({ ...m, [rx.id]: { pid: String((rx as any).patient_id ?? ''), name: rx.patient_name ?? 'patient', tid: trueId(rx), wid: String(rx.id) } }))
                          toggleShipSel(rx.id)
                        }}
                        style={{ verticalAlign: 'middle', marginRight: 5, cursor: 'pointer' }} />
                      {canQuickShip && (
                      <button type="button" title="Quick ship"
                        onClick={() => navigate(`/shipments?ship_rx=${rx.id}`)}
                        style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 5, padding: '3px 5px', cursor: 'pointer', lineHeight: 0, verticalAlign: 'middle' }}>
                        <Truck size={13} />
                      </button>
                      )}
                    </td>
                  )}
                  {visibleCols.map(col => (
                    <td key={col.key} style={col.key === 'sig' ? { ...tdStyle, maxWidth: 240 } : tdStyle}>
                      {col.key === 'rx_number' ? (<>
                        <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
                          {(rx as any).rx_number ?? '—'}
                        </span>
                        {!hdrHidden.refill && !subHidden.has('rx_number') && (rx as any).refill_number > 0 && <div style={mutedStyle}>refill {(rx as any).refill_number}</div>}
                        {/* D-WBFILL-3 §2.6: in a fill-grain list the ORIGINAL fill's row reads as
                            the bare Rx number — byte-for-byte the prescription's own number, and
                            the only row of the three with no subtitle. On Rx 104750 that is the row
                            staff opened when they meant the current fill, and two WorkBench edits
                            were recorded against a May dispense as a result. Name it. */}
                        {!hdrHidden.refill && !subHidden.has('rx_number')
                          && (rx as any).refill_number === 0
                          && (rx as any).rx_item_id && (rx as any).id !== (rx as any).rx_item_id
                          && <div style={mutedStyle}>original fill</div>}
                        {!subHidden.has('rx_number') && <DeliverySub rx={rx} />}
                      </>) : col.key === 'patient_name' ? (<>
                        <div style={{ fontWeight: 500 }}>{rx.patient_name}</div>
                      </>) : col.key === 'drug_name' ? (<>
                        <div style={{ fontWeight: 500 }}>{rx.drug_name}</div>
                        {!subHidden.has('drug_name') && <div style={mutedStyle}>{rx.drug_strength} · {rx.drug_form}</div>}
                      </>) : col.key === 'sig' ? (
                        <div style={truncStyle} title={rx.sig || undefined}>{rx.sig}</div>
                      ) : col.key === 'quantity_authorized' ? (
                        rx.quantity_filled > 0 && rx.quantity_filled < rx.quantity_authorized
                          ? <span>{rx.quantity_filled} of {rx.quantity_authorized} {rx.quantity_unit}</span>
                          : <span>{rx.quantity_authorized} {rx.quantity_unit}</span>
                      ) : col.key === 'prescriber_name' ? (
                        <div style={{ fontSize: 12 }}>{(rx as any).prescriber_name ?? '—'}</div>
                      ) : col.key === 'state' ? (
                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          {(rx as any).pioneerrx_rx_status
                            ? <ClinicalStatePill state={rx.state} label={(rx as any).pioneerrx_rx_status} />
                            : <ClinicalStatePill state={rx.state} />}
                        </span>
                      ) : col.key === 'payer_state' ? (
                        rx.insurance_carrier
                          ? <span style={{ fontSize: 12 }}>{rx.insurance_carrier}</span>
                          : <PayerStatePill state={rx.payer_state} />
                      ) : col.key.startsWith('wb_') && !WB_NON_EDITABLE.has(col.key) ? (
                        // Both members of WB_NON_EDITABLE are excluded HERE (the choice-dropdown
                        // branch) for their own reasons, and both fall through to their own
                        // `render` in the generic branch below:
                        // • wb_updated_at    — E-CV-202: a STAMP column, not an editable
                        //   field-def. Routing it here hit WorkBenchCell's no-def fallback,
                        //   which pills the RAW ISO. Renders via formatUsTimestamp. Read-only.
                        // • wb_task_due_date — ASP-64: a DATE field. It has NO entry in
                        //   workbench_field_defs (it is a physical rx_workbench column, mig 223),
                        //   so WorkBenchCell would hit that same no-def fallback and pill the raw
                        //   ISO — never a date picker. It renders its own WorkBenchDateCell,
                        //   which IS editable and writes via the same /rx/{id}/workbench PUT.
                        <WorkBenchCell
                          fieldDef={wbDefs[col.key.slice(3)]}
                          // POST THE ROW'S OWN ID. In a fill-grain row `id` IS the fill id, and
                          // that is exactly what the edit belongs to. This sent rx_item_id because
                          // the PUT once 404'd on a fill id — it no longer does: the GET and the
                          // PUT both resolve one through ResolveFillAsync. The stale workaround
                          // became the bug (Rx 149012): sending the prescription id leaves
                          // fillItemId null, so the write is prescription-grain and carries NO
                          // rx_fill_item_id stamp, and the timeline then falls back to the
                          // D-WBFILL-1 time window and files the edit under the NEWEST fill
                          // instead of the one the pharmacist was looking at.
                          rxId={rx.id}
                          current={(rx as any)[col.key] ?? null}
                        />
                      ) : col.key === 'last_state_change_at' ? (
                        <>{relativeTime(rx.last_state_change_at)}</>
                      ) : col.key.startsWith('cf:') ? (
                        <span style={{ fontSize: 12 }}>
                          <span title={cfDisp(rx)?.[col.key.slice(3)] || undefined} style={{ display: 'inline-block', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{formatCustomValue(cfDisp(rx)?.[col.key.slice(3)], col.dataType)}</span>
                        </span>
                      ) : (
                        // D-VIEWCOLS-1: generic branch for config-added standard columns
                        // (e.g. Date filled / Date written) — use the def's formatter.
                        <span style={{ fontSize: 12 }}>
                          {col.render ? col.render(rx) : (() => { const v = (col.getValue ? String(col.getValue(rx) ?? '') : String((rx as any)[col.key] ?? '')); return <span title={v || undefined} style={{ display: 'inline-block', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{v || '—'}</span> })()}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize}
                 onPage={setPage} showPageSize={false} label="prescriptions" />
          </>
        )}
      </div>
      {drawerId && (() => {
        const rx = drawerRx as any
        const fields: DrawerField[] = rx ? [
          { label: 'Patient', node: <span>{rx.patient_name ?? '—'}{(rx.patient_dob || rx.date_of_birth) ? <span style={{ color: 'var(--color-text-mute)' }}> · {formatUsDate(rx.patient_dob ?? rx.date_of_birth)}</span> : null}</span> },
          { label: 'Drug', node: <span>{rx.drug_name ?? '—'}{rx.drug_strength ? ` · ${rx.drug_strength}` : ''}{rx.drug_form ? ` ${rx.drug_form}` : ''}</span> },
          { label: 'Sig', node: <span>{rx.sig || '—'}</span> },
          { label: 'Quantity', node: <span>{rx.quantity_authorized ?? '—'} {rx.quantity_unit ?? ''}</span> },
          { label: 'Prescriber', node: <span>{rx.prescriber_name ?? '—'}</span> },
          { label: 'Status', node: <ClinicalStatePill state={rx.state} label={rx.pioneerrx_rx_status ?? undefined} /> },
          { label: 'Payer', node: rx.insurance_carrier ? <span>{rx.insurance_carrier}</span> : <PayerStatePill state={rx.payer_state} /> },
          { label: 'PMS queue', node: <span>{rx.current_tx_status_text || '—'}</span> },
          { label: 'PMS last update', node: <span>{relativeTime(rx.last_state_change_at)}</span> },
        ] : []
        const wbFields = rx ? [
          ...COLUMNS
            .filter(c => c.key.startsWith('wb_') && !WB_NON_EDITABLE.has(c.key))
            .map(c => {
              const key = c.key.slice(3)
              // Prefer what the endpoint just returned for THIS record; fall back to the row
              // while that first fetch is in flight so the drawer never opens empty.
              const fresh = drawerWb ? (drawerWb[key] ?? null) : undefined
              return { key, label: c.label, current: (fresh !== undefined ? fresh : (rx[c.key] ?? null)) as string | null }
            }),
          // ASP-64: Task Due Date in the right-click drawer too. It lives in WB_NON_EDITABLE
          // (its grid cell routes around the choice-dropdown branch), so the filter above
          // excludes it — appended explicitly; RxRowDrawer renders it via WorkBenchDateCell.
          { key: 'task_due_date', label: 'Task Due Date',
            current: ((drawerWb ? (drawerWb.task_due_date ?? null) : undefined) ?? rx.wb_task_due_date ?? null) as string | null },
        ] : []
        return (
          <RxRowDrawer
            title={rx ? String(rx.rx_number ?? 'Prescription') : 'Prescription'}
            subtitle={rx ? [rx.patient_name, rx.drug_name].filter(Boolean).join(' — ') : undefined}
            fields={fields}
            wbFields={wbFields}
            wbDefs={wbDefs}
            wbRxId={rx ? wbTargetId(rx) : null}
            openHref={`/rx/${drawerId}`}
            onClose={closeDrawer}
            notFound={!rx}
            // A drawer save must show in the GRID immediately, not on the next 15s poll —
            // the grid's wb cells are separate instances reading the row data, so refetch it.
            onWbSaved={(fieldKey, value) => {
              // PATCH THE ROW, then invalidate. Invalidation alone was not enough: a drawer edit
              // reliably wrote and the grid reliably did not move, because whether the list
              // actually refetched - and how soon - was left to query-cache timing. Writing the
              // saved value straight into the cached row makes the grid update from the edit
              // itself, which is the only thing that has to be true for the user.
              //
              // Safe because it writes exactly what the server accepted (this fires only after a
              // 200) into exactly the row that was edited, keyed on the id the PUT went to - the
              // FILL's id at fill grain. The invalidate still follows, so anything else the write
              // changed server-side (the parent projection, updated_at) arrives on the refetch.
              setWbEdits(prev => ({
                ...prev,
                [String(drawerId)]: { ...(prev[String(drawerId)] ?? {}), ['wb_' + fieldKey]: value },
              }))
              // Still refresh from the server: the write also moves things this row does not
              // carry (the parent projection, updated_at), and the override above clears itself
              // as soon as the fetched row agrees.
              // The drawer's own read is a separate query; without this it would keep showing
              // the value it fetched on open while the grid moved on.
              void queryClient.invalidateQueries({ queryKey: ['rx', 'workbench', drawerId] })
              void queryClient.invalidateQueries({ queryKey: ['rx'] })
            }}
          />
        )
      })()}
      {showBulk && (
        <BulkFieldEditor rxIds={bulkIds} wbIds={bulkWbIds}
          // BULK-2: the dialog shows the CHECKED ROWS' COMPLETE DATA (the grid's own visible
          // columns) at its bottom. Rows are resolved by TRUE id; one that refreshed out of
          // the loaded window renders as "…" cells.
          recordsTable={{
            columns: visibleCols.map(c => c.label),
            rows: bulkIds.map(tid => {
              const rx: any = items.find(r => (r as any).rx_item_id === tid || r.id === tid)
              return {
                id: tid,
                cells: visibleCols.map(col => {
                  if (!rx) return '…'
                  if (col.key === 'last_state_change_at') return relativeTime(rx.last_state_change_at) as string
                  if (col.key === 'wb_updated_at') return rx.wb_updated_at ? formatUsTimestamp(rx.wb_updated_at) : '—'
                  if (col.key === 'quantity_authorized') return `${rx.quantity_authorized ?? '—'} ${rx.quantity_unit ?? ''}`.trim()
                  if (col.key === 'state') return String(rx.pioneerrx_rx_status || rx.state || '—')
                  const raw = rx[col.key]
                  if (raw != null && raw !== '') return String(raw)
                  const got = col.getValue ? col.getValue(rx) : undefined
                  return got == null || got === '' ? '—' : String(got)
                }),
              }
            }),
          }}
          onClose={() => setShowBulk(false)}
          // Custom-field + workbench values feed several queries — refetch the rx family.
          onApplied={() => { void queryClient.invalidateQueries({ queryKey: ['rx'] }) }} />
      )}
    </>
  )
}

const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: 0 }
const subtitleStyle: React.CSSProperties = { margin: '4px 0 0', color: 'var(--color-text-soft)', fontSize: 13 }
const refreshBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '6px 8px', fontSize: 12, cursor: 'pointer', lineHeight: 0,
  border: '1px solid var(--color-border)', borderRadius: 6,
  background: 'var(--color-surface)', color: 'var(--color-text-soft)',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  overflow: 'hidden',
}
const tableScrollStyle: React.CSSProperties = {
  // D-PERF-2 (Patrick): the LIST scrolls, not the page — so the toolbar, tabs and
  // column headers stay put while rows move under them. Previously the whole
  // document scrolled, which pushed the header off-screen and meant you lost the
  // column labels the moment you went past the first screenful.
  //
  // 100dvh (not vh) because iOS Safari's vh includes the collapsing browser
  // chrome, which makes the list taller than the visible area and re-introduces a
  // page scrollbar on exactly the device Patrick tests on.
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 'calc(100dvh - 320px)',
  width: '100%',
  WebkitOverflowScrolling: 'touch',
  // Also the prerequisite for windowing: a fixed-height scroll container is what
  // lets us render ~50 rows instead of 23,635 (D-PERF-1 item 1).
  overscrollBehavior: 'contain',
}
const tableStyle: React.CSSProperties = {
  width: '100%', minWidth: 2200, borderCollapse: 'separate', borderSpacing: 0, fontSize: 13,
}
const thStyle: React.CSSProperties = {
  // Sticky within the scroll container above — column labels stay visible for
  // every row, which is the whole point of moving the scrollbar inside.
  // `sticky` also establishes the containing block the resize handle absolutely positions
  // against, so no extra `position: relative` is needed on the header cell.
  position: 'sticky',
  top: 0,
  zIndex: 2,
  // D-VIEWS-COLUMN-RESIZE: without this a narrowed header keeps its intrinsic width and the
  // drag appears to do nothing. Clip instead, so dragging visibly narrows the column.
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // NOTE: the existing --color-surface-alt background below is what makes the
  // sticky header opaque; rows would otherwise scroll visibly beneath it. Do not
  // add a second background here — that was a duplicate-key compile error.
  textAlign: 'left',
  padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--color-border)',
  // Rows open on DOUBLE-click now (the cursor is sensitive and single-click opened profiles by
  // accident), so a single click no longer navigates — it just places the caret for text selection.
  cursor: 'default',
}
// Zebra striping now lives in ONE place — `tbody tr:nth-child(even)` in index.css — so every
// table in the app stripes identically and this row map does not carry a private copy of the
// rule. Same rows, same token as the inline version this replaced.
const tdStyle: React.CSSProperties = {
  padding: '12px 14px', verticalAlign: 'middle',
  // Allow selecting/copying cell text (e.g. the Rx number) to paste into PioneerRx. Selection is
  // usable now that a single click no longer navigates away.
  userSelect: 'text',
}
const mutedStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--color-text-mute)', marginTop: 2,
}
const truncStyle: React.CSSProperties = {
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}



// Delivery state under the Rx number, on EVERY prescription queue. StationQueue
// backs All, Intake, Insurance, Production, Ready & Out, Hold, Refills and every
// custom view, so this renders once here instead of in seven places that drift.
//
// ship_status and ship_delivered_at already ride the row — they are registry
// columns the server pushes down for the Delivery filter — so no extra request.
// Nothing renders without a shipment: a blank 'not shipped' line would be noise
// on the queues where most rows have not shipped yet.
// Labels for every status dbo.rx_shipments allows (CK constraint: created,
// label_purchased, pending_confirmation, in_transit, out_for_delivery, delivered,
// exception, cancel_pending, cancelled). "label_purchased" is deliberately NOT
// called in transit — the package has not moved yet, and a courier calling the
// pharmacy about a late order needs that distinction.
const SHIP_LABEL: Record<string, { text: string; tone: 'ok' | 'warn' | 'bad' | 'mute' }> = {
  created:              { text: 'Label created',        tone: 'mute' },
  label_purchased:      { text: 'Label created',        tone: 'mute' },
  pending_confirmation: { text: 'Pending confirmation', tone: 'mute' },
  in_transit:           { text: 'In transit',           tone: 'warn' },
  out_for_delivery:     { text: 'Out for delivery',     tone: 'warn' },
  delivered:            { text: 'Delivered',            tone: 'ok'   },
  exception:            { text: 'Delivery issue',       tone: 'bad'  },
  cancel_pending:       { text: 'Cancel requested',     tone: 'bad'  },
  cancelled:            { text: 'Shipment cancelled',   tone: 'mute' },
}
const SHIP_TONE = {
  ok:   'var(--color-success, #15803d)',
  warn: 'var(--color-info, #1d4ed8)',
  bad:  'var(--color-danger, #b91c1c)',
  mute: 'var(--color-text-mute)',
}
function DeliverySub({ rx }: { rx: any }) {
  const rxId = rx?.id
  const status: string | null = rx?.ship_status ?? null
  if (!status) return null

  const known = SHIP_LABEL[status]
  const text = known?.text ?? status.replace(/_/g, ' ')
  const color = SHIP_TONE[known?.tone ?? 'mute']

  // Only a delivery carries a date worth showing inline; the rest change hourly
  // and a stale timestamp under the Rx number would read as fact.
  const when = status === 'delivered' && rx?.ship_delivered_at
    ? new Date(rx.ship_delivered_at).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })
    : null

  // A shipment the carrier has accepted has a document worth opening — this is
  // where the proof-of-delivery link lives now that the duplicate Status-column
  // chip is gone. An exception or cancellation has no document, so it stays text.
  const hasDoc = status !== 'exception' && status !== 'cancelled' && status !== 'cancel_pending'

  return (
    <div
      onClick={hasDoc ? (e => {
        e.stopPropagation()
        api.openRxPodDoc(rxId).catch(() => alert('Could not open the delivery document. Please try again.'))
      }) : undefined}
      title={hasDoc ? 'Open the delivery document' : `Delivery status: ${status}`}
      style={{
        fontSize: 11, marginTop: 1, color, fontWeight: 600,
        cursor: hasDoc ? 'pointer' : 'default',
        textDecoration: hasDoc ? 'underline dotted' : undefined,
      }}
    >
      {when ? `${text} ${when}` : text}
    </div>
  )
}
